/**
 * Reads a QR code from a playing <video>.
 *
 * Native BarcodeDetector where it really supports QR (Android Chrome), else
 * jsQR, loaded only then (every iPhone: Safari has no BarcodeDetector). The
 * jsQR path downscales to at most 960 px wide: enough pixels per module at the
 * 1280x720 the camera is asked for, without decoding a full frame each tick.
 */

export type Decode = (video: HTMLVideoElement) => Promise<string | null>;

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

const MAX_WIDTH = 960;

async function nativeDecoder(): Promise<Decode | null> {
  const Ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return null;
  try {
    // Some builds expose the class but cannot read QR codes.
    const formats = Ctor.getSupportedFormats ? await Ctor.getSupportedFormats() : ["qr_code"];
    if (!formats.includes("qr_code")) return null;
    const detector = new Ctor({ formats: ["qr_code"] });
    return async (video) => (await detector.detect(video))[0]?.rawValue ?? null;
  } catch {
    return null;
  }
}

async function jsqrDecoder(): Promise<Decode | null> {
  try {
    const { default: jsQR } = await import("jsqr");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    return async (video) => {
      const { videoWidth: w, videoHeight: h } = video;
      if (!w || !h) return null;
      const scale = Math.min(1, MAX_WIDTH / w);
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);
      if (canvas.width !== cw) canvas.width = cw;
      if (canvas.height !== ch) canvas.height = ch;
      ctx.drawImage(video, 0, 0, cw, ch);
      const image = ctx.getImageData(0, 0, cw, ch);
      return jsQR(image.data, cw, ch, { inversionAttempts: "dontInvert" })?.data ?? null;
    };
  } catch {
    return null;
  }
}

export async function createDecoder(): Promise<Decode | null> {
  return (await nativeDecoder()) ?? (await jsqrDecoder());
}
